import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import LaunchIcon from '@mui/icons-material/Launch'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import VideoCallIcon from '@mui/icons-material/VideoCall'
import RateReviewIcon from '@mui/icons-material/RateReview'
import DescriptionIcon from '@mui/icons-material/Description'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { Warning } from '@mui/icons-material'
import { CompareArrows } from '@mui/icons-material'

import { OrderData } from './OrdersTable'
import { OrderStatus } from '@/core/models/order.model'
import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import _ from 'lodash'
import tableNoData from '@/assets/svg/table-no-data.svg'
import dayjs from 'dayjs'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'

export type OrdersCardViewProps = {
  data: OrderData[]
  loading?: boolean
  onAcceptOrder?: (orderId: string) => void
  onRefuseOrder?: (orderId: string) => void
  onStartLocalConsultation?: (orderId: string, patientId: string) => void
  onCancelOrder?: (orderId: string) => void
  onRefresh?: () => void
  onConvertOrder?: (orderId: string) => void
}

const OrdersCardView = (props: OrdersCardViewProps) => {
  const { data, loading, onAcceptOrder, onRefuseOrder, onStartLocalConsultation, onCancelOrder, onRefresh, onConvertOrder } = props
  const { t } = useTranslation()

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case OrderStatus.Accepted:
        return 'success'
      case OrderStatus.Consultation_Created:
        return 'primary'
      case OrderStatus.Canceled:
      case OrderStatus.Refused:
        return 'error'
      case OrderStatus.Pending:
        return 'warning'
      default:
        return 'default'
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 4,
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <img src={tableNoData} alt="No data" style={{ width: 160, marginBottom: 16 }} />
        <Typography variant="body2" color="text.secondary">
          {t('common:table.noData')}
        </Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      {data.map((row) => (
        <Card
          key={row.id}
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: 1,
            },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Stack spacing={2}>
              {/* Header */}
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" fontWeight={600}>
                      {row.patient?.fullName}
                    </Typography>
                    {row.isPrioritized && (
                      <Tooltip title={t('page.meetingLink.ordersTable.tooltip.priority')}>
                        <Warning fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {row.speciality}
                  </Typography>
                  <Chip
                    label={t(`common:model.order.status.${_.camelCase(row.status)}.label`)}
                    size="small"
                    color={getStatusColor(row.status)}
                    sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                  />
                </Stack>

                <Stack direction="row" spacing={1}>
                  {row.status === OrderStatus.Pending && (
                    <>
                      <Tooltip title={t('common:button.accept.tooltip')}>
                        <IconButton size="small" color="success" onClick={() => onAcceptOrder?.(row.id)} sx={{ border: 1, borderColor: 'success.main' }}>
                          <CheckRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common:button.refuse.tooltip')}>
                        <IconButton size="small" color="error" onClick={() => onRefuseOrder?.(row.id)} sx={{ border: 1, borderColor: 'error.main' }}>
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <PopupState variant="popover" popupId={`order-${row.id}-actions`}>
                    {(popupState) => (
                      <>
                        <IconButton {...bindTrigger(popupState)} size="small">
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          {...bindMenu(popupState)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                          {row.status === OrderStatus.Accepted && (
                            <>
                              {row.meetingPlace !== AvailabilityMeetingPlace.VISIO_CONFERENCE && (
                                <MenuItem
                                  onClick={() => {
                                    if (row.patient) onStartLocalConsultation?.(row.id, row.patient.id)
                                    popupState.close()
                                  }}
                                >
                                  <ListItemIcon>
                                    <LaunchIcon fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText>{t('page.meetingLink.ordersTable.actions.startLocalConsultation')}</ListItemText>
                                </MenuItem>
                              )}
                              {!!row.patient && (
                                <MenuItem
                                  component={Link}
                                  to={`/consultations/create?orderId=${row.id}&patient=${row.patient.id}&speciality=${row.speciality}&startDate=${row.startDate}`}
                                >
                                  <ListItemIcon>
                                    <VideoCallIcon fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText>{t('page.meetingLink.ordersTable.actions.scheduleConsultation')}</ListItemText>
                                </MenuItem>
                              )}
                            </>
                          )}
                          {row.status === OrderStatus.Consultation_Created && (
                            <MenuItem component={Link} to={`/consultations/${row.consultationId}`}>
                              <ListItemIcon>
                                <LaunchIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>{t('page.meetingLink.ordersTable.actions.openConsultation')}</ListItemText>
                            </MenuItem>
                          )}
                          {![OrderStatus.Pending, OrderStatus.Canceled].includes(row.status as OrderStatus) && (
                            <MenuItem
                              onClick={() => {
                                onCancelOrder?.(row.id)
                                popupState.close()
                              }}
                            >
                              <ListItemIcon>
                                <CancelRoundedIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>{t('page.meetingLink.ordersTable.actions.cancelOrder')}</ListItemText>
                            </MenuItem>
                          )}
                          {onConvertOrder && (
                            <MenuItem
                              onClick={() => {
                                onConvertOrder(row.id)
                                popupState.close()
                              }}
                            >
                              <ListItemIcon>
                                <CompareArrows fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>{t('page.meetingLink.ordersTable.actions.convert')}</ListItemText>
                            </MenuItem>
                          )}
                        </Menu>
                      </>
                    )}
                  </PopupState>
                </Stack>
              </Stack>

              <Divider />

              {/* Info Section */}
              <Stack spacing={1.5}>
                {/* Date and Time */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {dayjs(row.startDate).format('DD/MM/YYYY')}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {dayjs(row.startDate).format('HH[h]mm')}
                  </Typography>
                </Stack>

                {/* Meeting Place */}
                <Stack direction="row" spacing={1} alignItems="center">
                  {row.meetingPlace === AvailabilityMeetingPlace.VISIO_CONFERENCE ? (
                    <VideoCallIcon className="text-blue-500" fontSize="small" />
                  ) : row.meetingPlace === AvailabilityMeetingPlace.SECOND_OPINION ? (
                    <RateReviewIcon className="text-purple-500" fontSize="small" />
                  ) : null}
                  <Typography variant="body2">{row.meetingPlaceDescription}</Typography>
                </Stack>

                {/* Files and Form */}
                {((row.filesCount ?? 0) > 0 || row.hasFormSubmission) && (
                  <Stack direction="row" spacing={2} alignItems="center">
                    {(row.filesCount ?? 0) > 0 && (
                      <Tooltip title={t('page.meetingLink.ordersTable.tooltip.files', { count: row.filesCount })}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <DescriptionIcon fontSize="small" className="text-blue-500" />
                          <Typography variant="body2">{row.filesCount}</Typography>
                        </Stack>
                      </Tooltip>
                    )}
                    {row.hasFormSubmission && (
                      <Tooltip title={t('page.meetingLink.ordersTable.tooltip.formSubmission')}>
                        <AssignmentIcon fontSize="small" className="text-green-500" />
                      </Tooltip>
                    )}
                  </Stack>
                )}

                {/* Comment */}
                {row.comment && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {row.comment}
                  </Typography>
                )}

                {/* Reference ID */}
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  {t('page.meetingLink.ordersTable.header.reference')}: {row.referenceId}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  )
}

export default OrdersCardView
